import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { MapPin, Clock, Heart, X, FileText, Image as ImageIcon, CheckCircle2, Hourglass } from "lucide-react";

export default function Foster() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [applying, setApplying] = useState(null);

  const load = () => {
    axios.get(`${API}/foster`).then(r => setItems(r.data.items));
    axios.get(`${API}/foster/my-applications`).then(r => setMyApps(r.data.items)).catch(() => setMyApps([]));
  };
  useEffect(() => { load(); }, []);

  const appliedFor = new Set(myApps.map(a => a.foster_id));

  return (
    <div className="space-y-6" data-testid="foster-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">Foster Program</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Open your home, save a life</h1>
        <p className="text-ink-soft text-sm max-w-2xl">Fostering means caring for a rescue temporarily — a few weeks of love can transform a pet's chance at adoption. NGOs cover medical costs. You provide the home.</p>
      </header>

      {myApps.length > 0 && (
        <section className="bg-surface border border-line rounded-3xl p-5" data-testid="my-foster-apps">
          <h2 className="font-heading text-lg font-extrabold">My applications</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {myApps.map(a => (
              <div key={a.application_id} className="bg-surface-muted/50 rounded-2xl p-3">
                <div className="flex items-center justify-between">
                  <span className="font-heading font-extrabold text-sm">Foster app · {a.foster_id.slice(-4)}</span>
                  <StatusPill status={a.status}/>
                </div>
                <div className="text-xs text-ink-soft mt-1">{a.days_available} days · {a.home_type || "—"}</div>
                {a.review_note && <div className="text-xs italic text-ink-soft mt-1">"{a.review_note}"</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(f => (
          <article key={f.foster_id} data-testid={`foster-${f.foster_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all">
            <img src={f.photo} alt={f.pet_name} className="w-full h-44 object-cover"/>
            <div className="p-5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-heading text-xl font-extrabold">{f.pet_name}</h3>
                <span className="text-xs uppercase tracking-wider font-bold bg-surface-muted rounded-full px-2 py-1">{f.species}</span>
              </div>
              <div className="text-xs text-ink-soft uppercase tracking-wider font-bold mt-1">{f.breed} · {f.age_years}y</div>
              <p className="text-sm text-ink-soft mt-2 line-clamp-3">{f.description}</p>
              <div className="mt-3 text-xs text-ink-soft flex items-center justify-between">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{f.city}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{f.duration_days} days</span>
              </div>
              {appliedFor.has(f.foster_id) ? (
                <div className="mt-4 w-full bg-surface-muted text-ink-soft rounded-full py-2.5 text-sm font-bold text-center">
                  <CheckCircle2 className="inline w-4 h-4 mr-1"/> Application submitted
                </div>
              ) : (
                <button data-testid={`apply-foster-${f.foster_id}`} onClick={() => setApplying(f)} className="mt-4 w-full bg-brand text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand-hover inline-flex items-center justify-center gap-1.5">
                  <Heart className="w-4 h-4"/> Apply to foster
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      {applying && <ApplyModal foster={applying} onClose={() => { setApplying(null); load(); }}/>}
      {user?.role === "ngo" && (
        <Link to="/app/ngo/foster-applications" className="block text-center text-sm font-bold text-brand hover:underline" data-testid="ngo-review-apps-link">
          Review foster applications →
        </Link>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = { pending: { l: "Pending", c: "bg-sun text-ink" }, under_review: { l: "Reviewing", c: "bg-brand-blue text-white" }, approved: { l: "Approved", c: "bg-brand-green text-white" }, rejected: { l: "Declined", c: "bg-ink text-white" } };
  const m = map[status] || map.pending;
  return <span className={`${m.c} rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>{m.l}</span>;
}

function ApplyModal({ foster, onClose }) {
  const [form, setForm] = useState({ message: "", days_available: foster.duration_days, home_type: "apartment", has_other_pets: false, phone: "" });
  const [idProof, setIdProof] = useState(null);
  const [homePhoto, setHomePhoto] = useState(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const readFile = (file, setter) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError("File must be < 2MB"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setter(e.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/foster/apply-v2`, { foster_id: foster.foster_id, ...form, id_proof_url: idProof, home_photo_url: homePhoto });
      setDone(true);
    } catch (err) {
      setError("Could not submit — please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft max-h-[90vh] overflow-y-auto" data-testid="foster-apply-form">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Foster {foster.pet_name}</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
        </div>
        {!done ? (
          <>
            <p className="text-xs text-ink-soft mb-3 inline-flex items-center gap-1"><Hourglass className="w-3 h-3"/> NGO will review your application within 48 hours.</p>
            <Field label="Why you'd be a great foster">
              <textarea data-testid="foster-message" value={form.message} onChange={e => set("message", e.target.value)} required rows={3} className="inp resize-none"/>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Days available"><input data-testid="foster-days" type="number" min="1" max="90" value={form.days_available} onChange={e => set("days_available", Number(e.target.value))} className="inp text-center"/></Field>
              <Field label="Home type">
                <select data-testid="foster-home-type" value={form.home_type} onChange={e => set("home_type", e.target.value)} className="inp">
                  <option value="apartment">Apartment</option><option value="independent">Independent</option><option value="farm">Farm/Large yard</option>
                </select>
              </Field>
            </div>
            <Field label="Phone (WhatsApp)"><input data-testid="foster-phone" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+91-…" className="inp"/></Field>
            <label className="flex items-center gap-2 mt-3 text-sm">
              <input type="checkbox" checked={form.has_other_pets} onChange={e => set("has_other_pets", e.target.checked)} data-testid="foster-other-pets"/>
              I have other pets at home
            </label>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <FileBlock icon={FileText} label="ID Proof" data={idProof} onChange={f => readFile(f, setIdProof)} testid="foster-id-proof"/>
              <FileBlock icon={ImageIcon} label="Home Photo" data={homePhoto} onChange={f => readFile(f, setHomePhoto)} testid="foster-home-photo"/>
            </div>
            {error && <p className="text-xs text-brand mt-2">{error}</p>}
            <button data-testid="foster-submit" className="mt-4 w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover">Submit application</button>
          </>
        ) : (
          <div className="text-center py-6">
            <Heart className="w-12 h-12 text-brand fill-brand mx-auto mb-2"/>
            <div className="font-heading text-xl font-extrabold">Application sent!</div>
            <p className="text-sm text-ink-soft mt-1">The NGO will reach out within 48 hours.</p>
            <button type="button" onClick={onClose} className="mt-4 bg-ink text-white rounded-full px-5 py-2 text-sm font-bold">Close</button>
          </div>
        )}
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-3">
      <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function FileBlock({ icon: Icon, label, data, onChange, testid }) {
  return (
    <label className={`block border-2 border-dashed rounded-2xl p-3 cursor-pointer text-center text-xs ${data ? "border-brand-green bg-leaf/20" : "border-line text-ink-soft hover:bg-surface-muted"}`}>
      <input data-testid={testid} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => onChange(e.target.files?.[0])}/>
      <Icon className="w-5 h-5 mx-auto mb-1"/>
      <div className="font-bold">{data ? "✓ Uploaded" : label}</div>
      <div className="text-[10px] opacity-70">{data ? "Tap to replace" : "Tap to upload (<2MB)"}</div>
    </label>
  );
}
